import React from 'react';
import { withRouter } from 'react-router-dom';
import CONSTANT from '../../constant';
import Loader from '../../component/common/Loader';
import { getHrEmployeeStatusReport, getHrManagerGradeReport } from '../../actions/AppraisalCycleAction';
import ResponsivePagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';

const PERIOD_STATUS_MAP: Record<string, { cls: string; label: string }> = {
    Draft:     { cls: 'badge-warning',   label: 'Draft' },
    Active:    { cls: 'badge-success',   label: 'Active' },
    Completed: { cls: 'badge-secondary', label: 'Completed' },
};

const OVERALL_STATUS_MAP: Record<string, { cls: string; label: string }> = {
    not_started:            { cls: 'badge-secondary', label: 'Not Started' },
    pending_kpi:            { cls: 'badge-warning',   label: 'Pending KPI Submission' },
    pending_rm_approval:    { cls: 'badge-warning',   label: 'Pending RM KPI Approval' },
    pending_rvm_approval:   { cls: 'badge-warning',   label: 'Pending RVM KPI Approval' },
    pending_self_appraisal: { cls: 'badge-info',      label: 'Pending Self Appraisal' },
    pending_reporting:      { cls: 'badge-info',      label: 'Pending Reporting Manager' },
    pending_reviewing:      { cls: 'badge-info',      label: 'Pending Reviewing Manager' },
    pending_accepting:      { cls: 'badge-info',      label: 'Pending Accepting Authority' },
    sent_back_by_rm:        { cls: 'badge-danger',    label: 'Sent Back by RM' },
    sent_back_by_rvm:       { cls: 'badge-danger',    label: 'Sent Back by RVM' },
    completed:              { cls: 'badge-success',   label: 'Completed' },
};

const styles: Record<string, React.CSSProperties> = {
    summaryCard: {
        borderRadius: 10,
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        background: '#fff',
        marginBottom: 4,
    },
    summaryIcon: {
        width: 44,
        height: 44,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
    },
    summaryLabel: { fontSize: 12, color: '#888', marginBottom: 2, fontWeight: 500 },
    summaryValue: { fontSize: 22, fontWeight: 700, lineHeight: 1 },
    empCell: { textAlign: 'left' as const, verticalAlign: 'middle' as const },
    empName: { fontWeight: 600, fontSize: 13, color: '#222', marginBottom: 2 },
    empNum: { fontSize: 11, color: '#888', fontWeight: 400 },
    periodPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: '#f0f4ff',
        borderRadius: 20,
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 500,
        color: '#3a5cbf',
        whiteSpace: 'nowrap' as const,
    },
    ongoingPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: '#e6f9ee',
        borderRadius: 20,
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 600,
        color: '#1a7a3e',
        whiteSpace: 'nowrap' as const,
    },
    truncCell: {
        maxWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        fontSize: 12,
        verticalAlign: 'middle' as const,
    },
    centeredTd: { textAlign: 'center' as const, verticalAlign: 'middle' as const },
    statsPanel: {
        border: '1px solid #e6e9f5',
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 16,
        background: '#fafbff',
    },
    statsSectionTitle: { fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 8 },
    statChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#fff',
        border: '1px solid #e6e9f5',
        borderRadius: 20,
        padding: '4px 12px',
        fontSize: 12,
        marginRight: 8,
        marginBottom: 8,
    },
    statChipValue: { fontWeight: 700 },
    rowspanTd: { verticalAlign: 'top' as const, background: '#fafbff', paddingTop: 14 },
    periodRowBorder: { borderTop: '1px dashed #dde3f7' },
    employeeGroupBorder: { borderTop: '2px solid #c8d6f0' },
    naText: { color: '#bbb', fontSize: 12 },
};

class HrDownloadReports extends React.Component<any, any> {
    private searchTimeout: any = null;

    constructor(props: any) {
        super(props);
        this.state = {
            activeTab: 'employee-status',
            showLoader: false,
            employeeStatusList: undefined,
            employeeStatusCount: 0,
            employeeCompletedCount: 0,
            employeePendingCount: 0,
            managerGradeList: undefined,
            managerGradeCount: 0,
            managerCompletedCount: 0,
            managerPendingCount: 0,
            searchTerm: '',
            aparStatusFilter: '',
            periodStatusFilter: '',
            overallStatusCounts: {},
            periodStatusCounts: {},
            showDetailedStats: false,
            page: 1,
            limit: 20,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        document.title = 'Download Reports - ' + CONSTANT.organisation.name;
        this.loadEmployeeStatusList();
    }

    componentWillUnmount() {
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
    }

    loadEmployeeStatusList = () => {
        const { page, limit, searchTerm, aparStatusFilter, periodStatusFilter } = this.state;
        this.setState({ showLoader: true });
        getHrEmployeeStatusReport({
            page, limit, search: searchTerm,
            aparStatus: aparStatusFilter || undefined,
            periodStatus: periodStatusFilter || undefined,
        }).then((res: any) => {
            this.setState({
                employeeStatusList: res?.result?.list || [],
                employeeStatusCount: res?.result?.count || 0,
                employeeCompletedCount: res?.result?.completedCount || 0,
                employeePendingCount: res?.result?.pendingCount || 0,
                overallStatusCounts: res?.result?.overallStatusCounts || {},
                periodStatusCounts: res?.result?.periodStatusCounts || {},
                showLoader: false,
            });
        }).catch(() => {
            this.setState({ employeeStatusList: [], employeeStatusCount: 0, showLoader: false });
        });
    };

    loadManagerGradeList = () => {
        const { page, limit, searchTerm, periodStatusFilter } = this.state;
        this.setState({ showLoader: true });
        getHrManagerGradeReport({
            page, limit, search: searchTerm,
            periodStatus: periodStatusFilter || undefined,
        }).then((res: any) => {
            this.setState({
                managerGradeList: res?.result?.list || [],
                managerGradeCount: res?.result?.count || 0,
                managerCompletedCount: res?.result?.completedCount || 0,
                managerPendingCount: res?.result?.pendingCount || 0,
                overallStatusCounts: {},
                periodStatusCounts: res?.result?.periodStatusCounts || {},
                showLoader: false,
            });
        }).catch(() => {
            this.setState({ managerGradeList: [], managerGradeCount: 0, showLoader: false });
        });
    };

    loadCurrentTab = () => {
        const { activeTab } = this.state;
        if (activeTab === 'employee-status') {
            this.loadEmployeeStatusList();
        } else {
            this.loadManagerGradeList();
        }
    };

    switchTab = (tab: string) => {
        this.setState({ activeTab: tab, searchTerm: '', aparStatusFilter: '', periodStatusFilter: '', page: 1 }, () => {
            this.loadCurrentTab();
        });
    };

    handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = e.target.value;
        this.setState({ searchTerm, page: 1 });
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.loadCurrentTab();
        }, 400);
    };

    handleAparStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ aparStatusFilter: e.target.value, page: 1 }, () => {
            this.loadCurrentTab();
        });
    };

    handlePeriodStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ periodStatusFilter: e.target.value, page: 1 }, () => {
            this.loadCurrentTab();
        });
    };

    toggleDetailedStats = () => {
        this.setState({ showDetailedStats: !this.state.showDetailedStats });
    };

    clearFilters = () => {
        this.setState({ searchTerm: '', aparStatusFilter: '', periodStatusFilter: '', page: 1 }, () => {
            this.loadCurrentTab();
        });
    };

    handlePageChange = (newPage: number) => {
        this.setState({ page: newPage }, () => {
            this.loadCurrentTab();
        });
    };

    formatDate = (dateStr: string | null) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr + 'T00:00:00');
            if (isNaN(d.getTime())) return dateStr;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = String(d.getFullYear()).slice(-2);
            return `${day}-${month}-${year}`;
        } catch { return dateStr; }
    };

    buildAndTriggerCsv = (reportType: string, list: any[]) => {
        const fmtCsv = (val: any) => {
            const s = String(val ?? '').replace(/"/g, '""');
            return `"${s}"`;
        };
        const fmtDate = (d: string | null) => fmtCsv(d ? this.formatDate(d) : '-');
        const fmtGrade = (g: any) => (g !== null && g !== undefined) ? parseFloat(g).toFixed(2) : 'N/A';

        const csvRows: string[] = [];

        if (reportType === 'employee-status') {
            csvRows.push([
                'S.No', 'Employee Number', 'Name', 'Unit', 'Department', 'Designation', 'Grade',
                'Period From', 'Period To', 'Period Status',
                'Reporting Manager', 'Reviewing Manager', 'Accepting Authority',
                'APAR Status',
            ].map(fmtCsv).join(','));
            let rowNum = 1;
            list.forEach((item: any) => {
                const periods: any[] = item.periods || [];
                if (periods.length === 0) {
                    csvRows.push([
                        rowNum++, fmtCsv(item.employeeNumber), fmtCsv(item.name),
                        fmtCsv(item.unitName), fmtCsv(item.departmentName), fmtCsv(item.designationName), fmtCsv(item.gradeName),
                        fmtCsv('-'), fmtCsv('-'), fmtCsv('-'),
                        fmtCsv('-'), fmtCsv('-'), fmtCsv('-'),
                        fmtCsv('No periods assigned'),
                    ].join(','));
                } else {
                    periods.forEach((p: any) => {
                        const aparStatus = OVERALL_STATUS_MAP[p.overallStatus]?.label || p.overallStatus || '-';
                        const periodStatus = PERIOD_STATUS_MAP[p.periodStatus]?.label || p.periodStatus || '-';
                        csvRows.push([
                            rowNum++,
                            fmtCsv(item.employeeNumber),
                            fmtCsv(item.name),
                            fmtCsv(item.unitName),
                            fmtCsv(item.departmentName),
                            fmtCsv(item.designationName),
                            fmtCsv(item.gradeName),
                            fmtDate(p.effectiveFrom),
                            p.effectiveTo ? fmtDate(p.effectiveTo) : fmtCsv('Ongoing'),
                            fmtCsv(periodStatus),
                            fmtCsv(p.reportingManager?.name || '-'),
                            fmtCsv(p.reviewingManager?.name || '-'),
                            fmtCsv(p.acceptingAuthority?.name || '-'),
                            fmtCsv(aparStatus),
                        ].join(','));
                    });
                }
            });
        } else {
            csvRows.push([
                'S.No', 'Employee Number', 'Name', 'Department', 'Designation',
                'Period From', 'Period To', 'Period Status',
                'Reporting Grade', 'Reviewing Grade', 'Acceptance Grade',
                'Training (Current Assignment)', 'Training (Future Career)',
                'Reporting Pen Picture', 'Reviewing Pen Picture',
                'Appraisee Submitted On', 'RO-1 Submitted On', 'RO-2 Submitted On', 'Accepting Authority Submitted On',
            ].map(fmtCsv).join(','));
            let rowNum = 1;
            list.forEach((item: any) => {
                const periods: any[] = item.periods || [];
                if (periods.length === 0) {
                    csvRows.push([
                        rowNum++,
                        fmtCsv(item.employeeNumber),
                        fmtCsv(item.name),
                        fmtCsv(item.departmentName),
                        fmtCsv(item.designationName),
                        fmtCsv('-'), fmtCsv('-'), fmtCsv('-'),
                        'N/A', 'N/A', 'N/A',
                        fmtCsv(''), fmtCsv(''), fmtCsv(''), fmtCsv(''),
                        fmtCsv('-'), fmtCsv('-'), fmtCsv('-'), fmtCsv('-'),
                    ].join(','));
                } else {
                    periods.forEach((p: any) => {
                        const periodStatus = PERIOD_STATUS_MAP[p.periodStatus]?.label || p.periodStatus || '-';
                        csvRows.push([
                            rowNum++,
                            fmtCsv(item.employeeNumber),
                            fmtCsv(item.name),
                            fmtCsv(item.departmentName),
                            fmtCsv(item.designationName),
                            fmtDate(p.effectiveFrom),
                            p.effectiveTo ? fmtDate(p.effectiveTo) : fmtCsv('Ongoing'),
                            fmtCsv(periodStatus),
                            fmtGrade(p.reportingGrade),
                            fmtGrade(p.reviewingGrade),
                            fmtGrade(p.acceptingGrade),
                            fmtCsv(p.trainingCurrentAssignment || ''),
                            fmtCsv(p.trainingFutureCareer || ''),
                            fmtCsv(p.reportingPenPicture || ''),
                            fmtCsv(p.reviewingPenPicture || ''),
                            fmtCsv(p.appraiseeSubmittedAt || '-'),
                            fmtCsv(p.reportingSubmittedAt || '-'),
                            fmtCsv(p.reviewingSubmittedAt || '-'),
                            fmtCsv(p.acceptingSubmittedAt || '-'),
                        ].join(','));
                    });
                }
            });
        }

        const csvContent = '\uFEFF' + csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportType}-report.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    handleDownload = (reportType: string) => {
        const { searchTerm, aparStatusFilter, periodStatusFilter } = this.state;
        this.setState({ showLoader: true });

        const fetchAll = reportType === 'employee-status'
            ? getHrEmployeeStatusReport({
                page: 1, limit: 100000, search: searchTerm,
                aparStatus: aparStatusFilter || undefined,
                periodStatus: periodStatusFilter || undefined,
            })
            : getHrManagerGradeReport({
                page: 1, limit: 100000, search: searchTerm,
                periodStatus: periodStatusFilter || undefined,
            });

        fetchAll.then((res: any) => {
            const list = res?.result?.list || [];
            this.setState({ showLoader: false });
            this.buildAndTriggerCsv(reportType, list);
        }).catch(() => {
            this.setState({ showLoader: false });
        });
    };

    renderTruncatedText = (text: string | null | undefined) => {
        if (!text) return <span style={styles.naText}>N/A</span>;
        return (
            <span title={text} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                {text}
            </span>
        );
    };

    getGradeBadge = (grade: number | null) => {
        if (grade === null || grade === undefined) {
            return <span style={styles.naText}>N/A</span>;
        }
        const val = parseFloat(String(grade));
        let cls = 'badge-secondary';
        if (val >= 80) cls = 'badge-success';
        else if (val >= 60) cls = 'badge-info';
        else if (val >= 40) cls = 'badge-warning';
        else cls = 'badge-danger';
        return <span className={`badge ${cls}`} style={{ fontSize: 12, padding: '4px 8px' }}>{val.toFixed(2)}</span>;
    };

    renderSubmittedDate = (value: string | null | undefined) => {
        if (!value) return <span style={styles.naText}>Pending</span>;
        const datePart = value.split(' ')[0];
        return <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{datePart}</span>;
    };

    renderManagerCell = (manager: any) => {
        if (!manager) return <span style={styles.naText}>N/A</span>;
        return <span style={{ fontWeight: 500, fontSize: 12 }}>{manager.name || 'N/A'}</span>;
    };

    renderPeriodCell = (effectiveFrom: string | null, effectiveTo: string | null) => {
        if (effectiveTo) {
            return (
                <span style={styles.periodPill}>
                    <i className="fa fa-calendar-o" style={{ fontSize: 10 }} />
                    {this.formatDate(effectiveFrom)} &mdash; {this.formatDate(effectiveTo)}
                </span>
            );
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                <span style={styles.periodPill}>
                    <i className="fa fa-calendar-o" style={{ fontSize: 10 }} />
                    {this.formatDate(effectiveFrom)}
                </span>
                <span style={styles.ongoingPill}>
                    <i className="fa fa-circle" style={{ fontSize: 7 }} />
                    Ongoing
                </span>
            </div>
        );
    };

    renderPeriodStatus = (status: string | null | undefined) => {
        if (!status) return <span style={styles.naText}>N/A</span>;
        const info = PERIOD_STATUS_MAP[status] || { cls: 'badge-secondary', label: status };
        return <span className={`badge ${info.cls} status-badge`}>{info.label}</span>;
    };

    renderSummaryCards = (total: number, completed: number, pending: number) => (
        <div className="row mb-3">
            <div className="col-12 col-sm-4 mb-2 mb-sm-0">
                <div style={styles.summaryCard}>
                    <div style={{ ...styles.summaryIcon, background: '#e8f0fe', color: '#3a5cbf' }}>
                        <i className="fa fa-users" />
                    </div>
                    <div>
                        <div style={styles.summaryLabel}>Total Employees</div>
                        <div style={{ ...styles.summaryValue, color: '#3a5cbf' }}>{total}</div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-4 mb-2 mb-sm-0">
                <div style={{ ...styles.summaryCard, borderLeft: '4px solid #28a745' }}>
                    <div style={{ ...styles.summaryIcon, background: '#e6f9ee', color: '#1a7a3e' }}>
                        <i className="fa fa-check-circle" />
                    </div>
                    <div>
                        <div style={styles.summaryLabel}>Completed</div>
                        <div style={{ ...styles.summaryValue, color: '#28a745' }}>{completed}</div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-4">
                <div style={{ ...styles.summaryCard, borderLeft: '4px solid #ffc107' }}>
                    <div style={{ ...styles.summaryIcon, background: '#fff8e1', color: '#b8860b' }}>
                        <i className="fa fa-clock-o" />
                    </div>
                    <div>
                        <div style={styles.summaryLabel}>Pending</div>
                        <div style={{ ...styles.summaryValue, color: '#b8860b' }}>{pending}</div>
                    </div>
                </div>
            </div>
        </div>
    );

    renderDetailedStats = (showOverall: boolean) => {
        const { showDetailedStats, overallStatusCounts, periodStatusCounts } = this.state;
        return (
            <div className="mb-3">
                <button
                    className="btn btn-link p-0"
                    style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                    onClick={this.toggleDetailedStats}
                >
                    <i className={`fa fa-chevron-${showDetailedStats ? 'down' : 'right'} mr-2`} style={{ fontSize: 10 }} />
                    Detailed Statistics
                </button>
                {showDetailedStats && (
                    <div style={styles.statsPanel} className="mt-2">
                        {showOverall && (
                            <div className="mb-3">
                                <div style={styles.statsSectionTitle}>APAR Status Breakdown</div>
                                <div>
                                    {Object.keys(OVERALL_STATUS_MAP).map((key) => {
                                        const info = OVERALL_STATUS_MAP[key];
                                        const count = overallStatusCounts?.[key] || 0;
                                        return (
                                            <span key={key} style={styles.statChip}>
                                                <span className={`badge ${info.cls}`} style={{ fontSize: 10 }}>{info.label}</span>
                                                <span style={styles.statChipValue}>{count}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div>
                            <div style={styles.statsSectionTitle}>Period Status Breakdown</div>
                            <div>
                                {Object.keys(PERIOD_STATUS_MAP).map((key) => {
                                    const info = PERIOD_STATUS_MAP[key];
                                    const count = periodStatusCounts?.[key] || 0;
                                    return (
                                        <span key={key} style={styles.statChip}>
                                            <span className={`badge ${info.cls}`} style={{ fontSize: 10 }}>{info.label}</span>
                                            <span style={styles.statChipValue}>{count}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { activeTab, showLoader, employeeStatusList, employeeStatusCount, managerGradeList, managerGradeCount, searchTerm, aparStatusFilter, periodStatusFilter, page, limit } = this.state;

        const currentCount = activeTab === 'employee-status' ? employeeStatusCount : managerGradeCount;
        const totalPages = Math.ceil(currentCount / limit);

        return (
            <div className="container-fluid mb-3">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card homedob">
                            {/* Page Header */}
                            <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 8 }}>
                                <h1 className="page-title mb-0">
                                    <i className="fa fa-download text-primary mr-2" />
                                    Download Reports
                                </h1>
                            </div>

                            {/* Tabs */}
                            <div className="row mt-3">
                                <div className="col-md-12">
                                    <ul className="nav nav-tabs" style={{ borderBottom: '2px solid #e0e7ff' }}>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'employee-status' ? 'active' : ''}`}
                                                onClick={() => this.switchTab('employee-status')}
                                            >
                                                <i className="fa fa-users mr-1" />
                                                Employee APAR Status
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'manager-grades' ? 'active' : ''}`}
                                                onClick={() => this.switchTab('manager-grades')}
                                            >
                                                <i className="fa fa-bar-chart mr-1" />
                                                MIS
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* Search, Filters & Download */}
                            <div className="row mt-3 align-items-center mb-2">
                                <div className="col-12 col-lg-8 mb-2 mb-lg-0">
                                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
                                        <div className="input-group" style={{ maxWidth: 260 }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search employee..."
                                                value={searchTerm}
                                                onChange={this.handleSearch}
                                            />
                                            <div className="input-group-append">
                                                <span className="input-group-text">
                                                    <i className="fa fa-search" />
                                                </span>
                                            </div>
                                        </div>

                                        {activeTab === 'employee-status' && (
                                            <select
                                                className="form-control"
                                                style={{ maxWidth: 220 }}
                                                value={aparStatusFilter}
                                                onChange={this.handleAparStatusFilterChange}
                                            >
                                                <option value="">All APAR Statuses</option>
                                                {Object.keys(OVERALL_STATUS_MAP).map((key) => (
                                                    <option key={key} value={key}>{OVERALL_STATUS_MAP[key].label}</option>
                                                ))}
                                            </select>
                                        )}

                                        <select
                                            className="form-control"
                                            style={{ maxWidth: 180 }}
                                            value={periodStatusFilter}
                                            onChange={this.handlePeriodStatusFilterChange}
                                        >
                                            <option value="">All Period Statuses</option>
                                            {Object.keys(PERIOD_STATUS_MAP).map((key) => (
                                                <option key={key} value={key}>{PERIOD_STATUS_MAP[key].label}</option>
                                            ))}
                                        </select>

                                        {(searchTerm || aparStatusFilter || periodStatusFilter) && (
                                            <button className="btn btn-link p-0" style={{ fontSize: 12 }} onClick={this.clearFilters}>
                                                <i className="fa fa-times mr-1" />
                                                Clear filters
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-12 col-lg-4 d-flex justify-content-lg-end">
                                    <button
                                        className="btn submit-button"
                                        onClick={() => this.handleDownload(activeTab)}
                                        disabled={activeTab === 'employee-status' ? !employeeStatusList?.length : !managerGradeList?.length}
                                    >
                                        <i className="fa fa-download mr-1" />
                                        Download CSV
                                    </button>
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div className="row mt-2">
                                <div className="col-md-12">
                                    {activeTab === 'employee-status' && this.renderEmployeeStatusTable(employeeStatusList)}
                                    {activeTab === 'manager-grades' && this.renderManagerGradeTable(managerGradeList)}
                                </div>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-4 px-3">
                                    <ResponsivePagination
                                        activeItemClassName="Pagi-number-ac"
                                        current={page}
                                        total={totalPages}
                                        onPageChange={this.handlePageChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {showLoader && <Loader />}
            </div>
        );
    }

    renderEmployeeStatusTable(list: any[] | undefined) {
        if (list === undefined) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x text-muted" />
                    <p className="mt-2 text-muted">Loading...</p>
                </div>
            );
        }

        const { employeeStatusCount, employeeCompletedCount, employeePendingCount } = this.state;

        return (
            <>
                {this.renderSummaryCards(employeeStatusCount, employeeCompletedCount, employeePendingCount)}
                {this.renderDetailedStats(true)}

                <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
                    <div className="table-wrapper radius-4">
                        <table className="tablen" style={{ width: '100%', minWidth: 1180 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 46, textAlign: 'center' }}>#</th>
                                    <th style={{ minWidth: 180 }}>Employee</th>
                                    <th style={{ minWidth: 120 }}>Unit</th>
                                    <th style={{ minWidth: 130 }}>Department</th>
                                    <th style={{ minWidth: 130 }}>Designation</th>
                                    <th style={{ minWidth: 100 }}>Grade</th>
                                    <th style={{ minWidth: 190 }}>Period</th>
                                    <th style={{ minWidth: 110 }}>Period Status</th>
                                    <th style={{ minWidth: 140 }}>Reporting Manager</th>
                                    <th style={{ minWidth: 140 }}>Reviewing Manager</th>
                                    <th style={{ minWidth: 140 }}>Accepting Authority</th>
                                    <th style={{ minWidth: 160 }}>APAR Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.length === 0 && (
                                    <tr>
                                        <td colSpan={12} className="text-center py-4 text-muted">No records found</td>
                                    </tr>
                                )}
                                {list.map((item: any, idx: number) => {
                                    const periods: any[] = item.periods || [];
                                    const rowCount = Math.max(periods.length, 1);

                                    if (periods.length === 0) {
                                        return (
                                            <tr key={item.employeeCode || idx}>
                                                <td className="sno" style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                <td style={styles.empCell}>
                                                    <div style={styles.empName}>{item.name}</div>
                                                    <div style={styles.empNum}>Emp# {item.employeeNumber}</div>
                                                </td>
                                                <td style={styles.truncCell}>{this.renderTruncatedText(item.unitName)}</td>
                                                <td style={styles.truncCell}>{this.renderTruncatedText(item.departmentName)}</td>
                                                <td style={styles.truncCell}>{this.renderTruncatedText(item.designationName)}</td>
                                                <td style={styles.truncCell}>{this.renderTruncatedText(item.gradeName)}</td>
                                                <td colSpan={6} className="text-muted" style={{ fontSize: 12 }}>No periods assigned</td>
                                            </tr>
                                        );
                                    }

                                    return periods.map((period: any, pIdx: number) => {
                                        const statusInfo = OVERALL_STATUS_MAP[period.overallStatus] || { cls: 'badge-secondary', label: period.overallStatus || 'Unknown' };
                                        const trStyle = pIdx === 0
                                            ? (idx > 0 ? styles.employeeGroupBorder : undefined)
                                            : styles.periodRowBorder;
                                        return (
                                            <tr key={`${item.employeeCode}-${pIdx}`} style={trStyle}>
                                                {pIdx === 0 && (
                                                    <>
                                                        <td className="sno" rowSpan={rowCount} style={{ textAlign: 'center', ...styles.rowspanTd }}>{idx + 1}</td>
                                                        <td rowSpan={rowCount} style={{ ...styles.empCell, ...styles.rowspanTd }}>
                                                            <div style={styles.empName}>{item.name}</div>
                                                            <div style={styles.empNum}>Emp# {item.employeeNumber}</div>
                                                        </td>
                                                        <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                            {this.renderTruncatedText(item.unitName)}
                                                        </td>
                                                        <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                            {this.renderTruncatedText(item.departmentName)}
                                                        </td>
                                                        <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                            {this.renderTruncatedText(item.designationName)}
                                                        </td>
                                                        <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                            {this.renderTruncatedText(item.gradeName)}
                                                        </td>
                                                    </>
                                                )}
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {this.renderPeriodCell(period.effectiveFrom, period.effectiveTo)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                    {this.renderPeriodStatus(period.periodStatus)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', fontSize: 12 }}>{this.renderManagerCell(period.reportingManager)}</td>
                                                <td style={{ verticalAlign: 'middle', fontSize: 12 }}>{this.renderManagerCell(period.reviewingManager)}</td>
                                                <td style={{ verticalAlign: 'middle', fontSize: 12 }}>{this.renderManagerCell(period.acceptingAuthority)}</td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <span className={`badge ${statusInfo.cls} status-badge`}>{statusInfo.label}</span>
                                                </td>
                                            </tr>
                                        );
                                    });
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    renderManagerGradeTable(list: any[] | undefined) {
        if (list === undefined) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x text-muted" />
                    <p className="mt-2 text-muted">Loading...</p>
                </div>
            );
        }

        const { managerGradeCount, managerCompletedCount, managerPendingCount } = this.state;

        return (
            <>
                {this.renderSummaryCards(managerGradeCount, managerCompletedCount, managerPendingCount)}
                {this.renderDetailedStats(false)}

                <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
                    <div className="table-wrapper radius-4">
                        <table className="tablen" style={{ width: '100%', minWidth: 1960 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 46, textAlign: 'center' }}>#</th>
                                    <th style={{ width: 90 }}>Emp #</th>
                                    <th style={{ minWidth: 160 }}>Employee Name</th>
                                    <th style={{ minWidth: 130 }}>Department</th>
                                    <th style={{ minWidth: 130 }}>Designation</th>
                                    <th style={{ minWidth: 190 }}>Period</th>
                                    <th style={{ minWidth: 110 }}>Period Status</th>
                                    <th style={{ width: 110, textAlign: 'center' }}>Reporting Grade</th>
                                    <th style={{ width: 110, textAlign: 'center' }}>Reviewing Grade</th>
                                    <th style={{ width: 110, textAlign: 'center' }}>Acceptance Grade</th>
                                    <th style={{ minWidth: 160 }}>Training (Current)</th>
                                    <th style={{ minWidth: 160 }}>Training (Future)</th>
                                    <th style={{ minWidth: 190 }}>Reporting Pen Picture</th>
                                    <th style={{ minWidth: 190 }}>Reviewing Pen Picture</th>
                                    <th style={{ minWidth: 140 }}>Appraisee Submitted On</th>
                                    <th style={{ minWidth: 140 }}>RO-1 Submitted On</th>
                                    <th style={{ minWidth: 140 }}>RO-2 Submitted On</th>
                                    <th style={{ minWidth: 140 }}>Accepting Authority Submitted On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.length === 0 && (
                                    <tr>
                                        <td colSpan={18} className="text-center py-4 text-muted">No records found</td>
                                    </tr>
                                )}
                                {list.map((item: any, idx: number) => {
                                    const periods: any[] = item.periods || [];
                                    const rowCount = Math.max(periods.length, 1);

                                    if (periods.length === 0) {
                                        return (
                                            <tr key={item.employeeCode || idx}>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{idx + 1}</td>
                                                <td style={{ verticalAlign: 'middle', fontSize: 12 }}>{item.employeeNumber}</td>
                                                <td style={styles.empCell}>
                                                    <div style={styles.empName}>{item.name}</div>
                                                </td>
                                                <td style={{ ...styles.truncCell }}>{this.renderTruncatedText(item.departmentName)}</td>
                                                <td style={{ ...styles.truncCell }}>{this.renderTruncatedText(item.designationName)}</td>
                                                <td colSpan={13} className="text-muted" style={{ fontSize: 12 }}>No periods assigned</td>
                                            </tr>
                                        );
                                    }

                                    return periods.map((p: any, pIdx: number) => {
                                        const trStyle = pIdx === 0
                                            ? (idx > 0 ? styles.employeeGroupBorder : undefined)
                                            : styles.periodRowBorder;
                                        return (
                                        <tr
                                            key={`${item.employeeCode}-${pIdx}`}
                                            style={trStyle}
                                        >
                                            {pIdx === 0 && (
                                                <>
                                                    <td rowSpan={rowCount} style={{ textAlign: 'center', ...styles.rowspanTd }}>{idx + 1}</td>
                                                    <td rowSpan={rowCount} style={{ fontSize: 12, ...styles.rowspanTd }}>{item.employeeNumber}</td>
                                                    <td rowSpan={rowCount} style={{ ...styles.empCell, ...styles.rowspanTd }}>
                                                        <div style={styles.empName}>{item.name}</div>
                                                    </td>
                                                    <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                        {this.renderTruncatedText(item.departmentName)}
                                                    </td>
                                                    <td rowSpan={rowCount} style={{ ...styles.truncCell, ...styles.rowspanTd }}>
                                                        {this.renderTruncatedText(item.designationName)}
                                                    </td>
                                                </>
                                            )}
                                            <td style={{ verticalAlign: 'middle' }}>
                                                {this.renderPeriodCell(p.effectiveFrom, p.effectiveTo)}
                                            </td>
                                            <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                {this.renderPeriodStatus(p.periodStatus)}
                                            </td>
                                            <td style={styles.centeredTd}>{this.getGradeBadge(p.reportingGrade)}</td>
                                            <td style={styles.centeredTd}>{this.getGradeBadge(p.reviewingGrade)}</td>
                                            <td style={styles.centeredTd}>{this.getGradeBadge(p.acceptingGrade)}</td>
                                            <td style={styles.truncCell}>{this.renderTruncatedText(p.trainingCurrentAssignment)}</td>
                                            <td style={styles.truncCell}>{this.renderTruncatedText(p.trainingFutureCareer)}</td>
                                            <td style={styles.truncCell}>{this.renderTruncatedText(p.reportingPenPicture)}</td>
                                            <td style={styles.truncCell}>{this.renderTruncatedText(p.reviewingPenPicture)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{this.renderSubmittedDate(p.appraiseeSubmittedAt)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{this.renderSubmittedDate(p.reportingSubmittedAt)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{this.renderSubmittedDate(p.reviewingSubmittedAt)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{this.renderSubmittedDate(p.acceptingSubmittedAt)}</td>
                                        </tr>
                                    );
                                    });
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }
}

export default withRouter(HrDownloadReports);
